import { CourseType } from '@/entities/Courses';
import type { UserShortInfo } from '@/shared/types/common';
import type { FileInfo } from '@/shared/types/files';

// Enum-like types (согласовано с API student-profile / my-profile)
export type Salutation = 'Mr' | 'Mrs' | 'Miss' | 'Ms' | 'Other';

export type Gender = 'Male' | 'Female' | 'Other' | 'Prefer not to say';

export type EmergencyRelationship =
  | 'Parent'
  | 'Spouse'
  | 'Partner'
  | 'Sibling'
  | 'Friend'
  | 'Other';

export type EnglishLevel =
  | 'TOEFL'
  | 'IELTS'
  | 'PEARSON'
  | 'Duolingo'
  | 'Previously studied or worked in English'
  | 'Native Speaker'
  | 'Other'
  | 'No test';

export type HowDidYouHear =
  | 'website'
  | 'social-media'
  | 'friend'
  | 'teacher'
  | 'counselor'
  | 'advertisement'
  | 'event'
  | 'search-engine'
  | 'other';

/** GET my-profile — student `profile` (MyProfileStudentProfile) */
export interface IStudentProfileData {
  id?: number;
  salutation?: Salutation | null;
  salutationOther?: string | null;
  gender?: Gender | null;
  genderOther?: string | null;
  dateOfBirth?: string | null;
  nationality?: string | null;
  countryOfBirth?: string | null;
  residenceUKFile?: FileInfo | null;
  photoIdFile?: FileInfo | null;
  visaCode?: string | null;
  visaFile?: FileInfo | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  emergencyFirstName?: string | null;
  emergencyLastName?: string | null;
  emergencyPhone?: string | null;
  emergencyRelationship?: string | null;
  emergencyRelationshipOther?: string | null;
  academicExperiences?: unknown[];
  cvFile?: FileInfo | null;
  motivationLetter?: FileInfo | null;
  howDidYouHearAboutTheCollege?: HowDidYouHear | null;
  howDidYouHearAboutTheCollegeOther?: string | null;
  supportingStatement?: string | null;
  hasSpecialNeeds?: boolean;
  additionalNeeds?: string | null;
  englishLevel?: EnglishLevel | null;
  englishLevelOther?: string | null;
  englishLevelFile?: FileInfo | null;
  isDeleted?: boolean;
}

export enum AccountType {
  Student = 'student',
  Staff = 'staff',
}

export interface IStudentProfileResponse {
  id: number;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatar: string | null;
  accountType: AccountType.Student;
  pushNotificationsEnabled: boolean;
  agreedToMarketing: boolean;
  profile: IStudentProfileData | null;
}

/** Курс в блоке assignedCourses staff-профиля */
export interface IStaffProfileAssignedCourse {
  id: number;
  title: string;
  type: CourseType;
  category: string | null;
}

/** GET my-profile — staff `profile` (MyProfileStaffProfile) */
export interface IStaffProfileData {
  id: number;
  jobTitle: string | null;
  city: string | null;
  address: string | null;
  businessEmail: string | null;
  about: string | null;
  emergencyFirstName: string | null;
  emergencyLastName: string | null;
  emergencyPhone: string | null;
  emergencyRelationship: string | null;
  emergencyRelationshipOther: string | null;
  annualLeaveEntitlementDays: number;
  socialLinks: string | null;
  assignedCoursesCount: number;
  isDeleted: boolean;
  lineManager: UserShortInfo | null;
  assignedCourses: IStaffProfileAssignedCourse[];
  cvFile: FileInfo | null;
  personalDocuments: FileInfo[];
  diplomas: FileInfo[];
  department?: string | null;
}

export interface ITeacherProfileResponse {
  id: number;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  avatar: string | null;
  accountType: AccountType.Staff;
  agreedToMarketing: boolean;
  profile: IStaffProfileData | null;
}

export type IProfileResponse = IStudentProfileResponse | ITeacherProfileResponse;

// Update requests
export interface IStudentProfileUpdate {
  firstName?: string;
  lastName?: string;
  phone?: string;
  pushNotificationsEnabled?: boolean;
  agreedToMarketing?: boolean;
  avatar?: File;
  salutation?: Salutation;
  salutationOther?: string | null;
  gender?: Gender;
  genderOther?: string | null;
  dateOfBirth?: string | null;
  nationality?: string | null;
  countryOfBirth?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  emergencyPhone?: string | null;
  emergencyRelationship?: EmergencyRelationship | null;
  emergencyRelationshipOther?: string | null;
  hasSpecialNeeds?: boolean | null;
  additionalNeeds?: string | null;
  englishLevel?: EnglishLevel | null;
  englishLevelOther?: string | null;
}

export interface ITeacherAdminProfileUpdate {
  firstName?: string;
  lastName?: string;
  phone?: string;
  agreedToMarketing?: boolean;
  avatar?: File;
  department?: string;
}

export type IProfileUpdate = IStudentProfileUpdate | ITeacherAdminProfileUpdate;

export interface IEmergencyContactUpdate {
  emergencyFirstName?: string | null;
  emergencyLastName?: string | null;
  emergencyPhone?: string | null;
  emergencyRelationship?: EmergencyRelationship | null;
  emergencyRelationshipOther?: string | null;
}

export interface IChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface IChangePasswordResponse {
  message: string;
}

export interface IDeleteAccountRequest {
  password: string;
}

/** Удаление с подтверждением по почте: ссылка уходит на адрес учётной записи. */
export interface IRequestAccountDeletionByEmailRequest {
  email: string;
}

/** Заявка на удаление: статус дальше читается без авторизации по `requestId`. */
export interface IDeleteAccountResponse {
  requestId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  requestedAt: string;
  completedAt: string | null;
}
